import { useCallback, useEffect, useMemo, useState } from "react";
import {
  createReservationByAdmin,
  deleteReservationForAdmin,
  fetchReservationsForAdmin,
  fetchReservationSettingsForAdmin,
  saveReservationSettingsForAdmin,
  updateReservationStatus,
} from "../../utils/reservationApi";
import { apiRequest } from "../../utils/apiClient";
import {
  fetchOpeningHoursForAdmin,
  saveOpeningHoursToApi,
} from "../../utils/contentApi";
// Appel API pour capturer l'empreinte Stripe
async function captureDepositForReservation(reservationId) {
  const data = await apiRequest(
    `/admin/reservations/${reservationId}/capture-deposit`,
    {
      method: "POST",
    },
  );
  return data;
}

const STATUS_OPTIONS = ["pending", "confirmed", "cancelled", "no_show"];
const TYPE_OPTIONS = ["all", "table", "chambre"];
const SORT_OPTIONS = [
  { value: "requestedDateAsc", label: "Date/heure croissante" },
  { value: "requestedDateDesc", label: "Date/heure decroissante" },
  { value: "createdDesc", label: "Plus recentes d'abord" },
];
const SOURCE_OPTIONS = [
  { value: "telephone", label: "Telephone" },
  { value: "sur_place", label: "Sur place" },
  { value: "instagram", label: "Instagram" },
  { value: "facebook", label: "Facebook" },
  { value: "whatsapp", label: "WhatsApp" },
  { value: "email", label: "Email" },
  { value: "site", label: "Site web" },
  { value: "autre", label: "Autre" },
];

const ADMIN_MENUS = [
  { id: "requests", label: "Reservations" },
  { id: "create", label: "Creation" },
  { id: "services", label: "Reglages services" },
  { id: "rooms", label: "Reglages chambres" },
];

const DEFAULT_RULES = {
  tableServices: [
    {
      id: "midi",
      label: "Midi",
      start: "11:00",
      end: "13:00",
      maxCovers: 40,
      maxCoversPerSlot: 8,
      slotIntervalMinutes: 5,
    },
    {
      id: "soir",
      label: "Soir",
      start: "18:00",
      end: "20:00",
      maxCovers: 40,
      maxCoversPerSlot: 8,
      slotIntervalMinutes: 5,
    },
  ],
  roomOptions: [
    { id: "chambre-1", label: "Chambre 1", isActive: true },
    { id: "chambre-2", label: "Chambre 2", isActive: true },
    { id: "chambre-3", label: "Chambre 3", isActive: true },
  ],
};

const TIME_REGEX = /^([01]\d|2[0-3]):[0-5]\d$/;

const DEFAULT_OPENING_HOURS = {
  days: [
    {
      dayIndex: 1,
      label: "Lundi",
      isClosed: false,
      slots: [{ open: "09:00", close: "15:00" }],
    },
    {
      dayIndex: 2,
      label: "Mardi",
      isClosed: false,
      slots: [{ open: "09:00", close: "15:00" }],
    },
    {
      dayIndex: 3,
      label: "Mercredi",
      isClosed: false,
      slots: [{ open: "09:00", close: "15:00" }],
    },
    {
      dayIndex: 4,
      label: "Jeudi",
      isClosed: false,
      slots: [{ open: "09:00", close: "15:00" }],
    },
    {
      dayIndex: 5,
      label: "Vendredi",
      isClosed: false,
      slots: [
        { open: "09:00", close: "15:00" },
        { open: "18:00", close: "00:00" },
      ],
    },
    { dayIndex: 6, label: "Samedi", isClosed: true, slots: [] },
    {
      dayIndex: 0,
      label: "Dimanche",
      isClosed: false,
      slots: [{ open: "10:00", close: "16:00" }],
    },
  ],
};

function getTodayIsoDate() {
  const today = new Date();
  const year = today.getFullYear();
  const month = String(today.getMonth() + 1).padStart(2, "0");
  const day = String(today.getDate()).padStart(2, "0");
  return `${year}-${month}-${day}`;
}

function slugify(input) {
  return String(input || "")
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-+|-+$/g, "")
    .slice(0, 40);
}

function getFirstServiceId(rules) {
  return Array.isArray(rules?.tableServices) && rules.tableServices.length > 0
    ? rules.tableServices[0].id
    : "";
}

function getFirstActiveRoomId(rules) {
  const rooms = Array.isArray(rules?.roomOptions) ? rules.roomOptions : [];
  const activeRoom = rooms.find((roomItem) => roomItem.isActive !== false);
  return activeRoom?.id || "";
}

function getServiceSlotTimes(service) {
  const start = String(service?.start || "");
  const end = String(service?.end || "");
  const interval = Number.parseInt(service?.slotIntervalMinutes, 10);

  if (!TIME_REGEX.test(start) || !TIME_REGEX.test(end)) {
    return [];
  }

  if (!Number.isInteger(interval) || interval < 5 || interval > 60) {
    return [];
  }

  const [startHour, startMinute] = start.split(":").map(Number);
  const [endHour, endMinute] = end.split(":").map(Number);

  const startMinutes = startHour * 60 + startMinute;
  const endMinutes = endHour * 60 + endMinute;

  if (startMinutes > endMinutes) {
    return [];
  }

  const slots = [];
  for (let value = startMinutes; value <= endMinutes; value += interval) {
    const hour = String(Math.floor(value / 60)).padStart(2, "0");
    const minute = String(value % 60).padStart(2, "0");
    slots.push(`${hour}:${minute}`);
  }

  return slots;
}

function buildCreateInitialState(rules) {
  const firstService = Array.isArray(rules?.tableServices)
    ? rules.tableServices[0]
    : null;
  const slots = getServiceSlotTimes(firstService);

  return {
    reservationType: "table",
    firstName: "",
    lastName: "",
    email: "",
    phone: "",
    requestedDate: getTodayIsoDate(),
    tableServiceId: getFirstServiceId(rules),
    requestedTime: slots[0] || String(firstService?.start || ""),
    guestCount: "2",
    nightCount: "1",
    roomId: getFirstActiveRoomId(rules),
    sourceOption: "telephone",
    sourceCustom: "",
    status: "confirmed",
    message: "",
  };
}

function buildSettingsForm(rules) {
  return {
    tableServices: Array.isArray(rules?.tableServices)
      ? rules.tableServices.map((serviceItem, index) => ({
          id: String(serviceItem?.id || `service-${index + 1}`).trim(),
          label: String(serviceItem?.label || `Service ${index + 1}`).trim(),
          start: String(serviceItem?.start || "").trim(),
          end: String(serviceItem?.end || "").trim(),
          maxCovers: String(serviceItem?.maxCovers || "40"),
          maxCoversPerSlot: String(serviceItem?.maxCoversPerSlot || "8"),
          slotIntervalMinutes: String(serviceItem?.slotIntervalMinutes || "5"),
        }))
      : [],
    roomOptions: Array.isArray(rules?.roomOptions)
      ? rules.roomOptions.map((roomItem, index) => ({
          id: String(roomItem?.id || `chambre-${index + 1}`).trim(),
          label: String(roomItem?.label || `Chambre ${index + 1}`).trim(),
          isActive: roomItem?.isActive !== false,
        }))
      : [],
  };
}

function createNewService(nextIndex) {
  const index = Number(nextIndex || 1);
  return {
    id: `service-${index}`,
    label: `Service ${index}`,
    start: "12:00",
    end: "13:30",
    maxCovers: "30",
    maxCoversPerSlot: "8",
    slotIntervalMinutes: "5",
  };
}

function createNewRoom(nextIndex) {
  const index = Number(nextIndex || 1);
  return {
    id: `chambre-${index}`,
    label: `Chambre ${index}`,
    isActive: true,
  };
}

function formatDate(value) {
  if (!value) {
    return "-";
  }

  try {
    return new Date(value).toLocaleDateString("fr-FR", {
      year: "numeric",
      month: "2-digit",
      day: "2-digit",
    });
  } catch (_error) {
    return "-";
  }
}

function formatDateTime(value) {
  if (!value) {
    return "-";
  }

  try {
    return new Date(value).toLocaleString("fr-FR", {
      year: "numeric",
      month: "2-digit",
      day: "2-digit",
      hour: "2-digit",
      minute: "2-digit",
    });
  } catch (_error) {
    return "-";
  }
}

function formatCurrency(cents, currency = "eur") {
  const amount = Math.max(0, Number(cents || 0)) / 100;
  try {
    return new Intl.NumberFormat("fr-FR", {
      style: "currency",
      currency: String(currency || "eur").toUpperCase(),
    }).format(amount);
  } catch (_error) {
    return `${amount.toFixed(2)} EUR`;
  }
}

function getDepositStatusLabel(status) {
  const normalized = String(status || "").toLowerCase();
  if (normalized === "requires_capture") {
    return "Empreinte autorisee";
  }
  if (normalized === "captured") {
    return "Capturee";
  }
  if (normalized === "released") {
    return "Liberee";
  }
  if (normalized === "failed") {
    return "Echec";
  }
  return "Non requise";
}

function validateServiceForm(service) {
  const label = String(service?.label || "").trim();
  const start = String(service?.start || "").trim();
  const end = String(service?.end || "").trim();
  const maxCovers = Number.parseInt(service?.maxCovers, 10);
  const maxPerSlot = Number.parseInt(service?.maxCoversPerSlot, 10);
  const interval = Number.parseInt(service?.slotIntervalMinutes, 10);

  if (!label) {
    return "Chaque service doit avoir un nom.";
  }

  if (!TIME_REGEX.test(start) || !TIME_REGEX.test(end)) {
    return "Les horaires des services doivent etre au format HH:mm.";
  }

  if (start > end) {
    return "L'heure de debut doit etre avant l'heure de fin.";
  }

  if (!Number.isInteger(maxCovers) || maxCovers < 1 || maxCovers > 500) {
    return "Le maximum de couverts du service doit etre entre 1 et 500.";
  }

  if (!Number.isInteger(maxPerSlot) || maxPerSlot < 1 || maxPerSlot > 100) {
    return "Le maximum de couverts par creneau doit etre entre 1 et 100.";
  }

  if (!Number.isInteger(interval) || interval < 5 || interval > 60) {
    return "L'intervalle des creneaux doit etre entre 5 et 60 minutes.";
  }

  return "";
}

function validateSettingsForm(settingsForm) {
  if (
    !Array.isArray(settingsForm.tableServices) ||
    settingsForm.tableServices.length === 0
  ) {
    return "Ajoutez au moins un service de table.";
  }

  if (
    !Array.isArray(settingsForm.roomOptions) ||
    settingsForm.roomOptions.length === 0
  ) {
    return "Ajoutez au moins une chambre.";
  }

  const seenServiceIds = new Set();
  for (const serviceItem of settingsForm.tableServices) {
    const serviceId = String(serviceItem.id || "").trim();
    if (!serviceId) {
      return "Chaque service doit avoir un identifiant valide.";
    }

    if (seenServiceIds.has(serviceId)) {
      return "Les identifiants des services doivent etre uniques.";
    }
    seenServiceIds.add(serviceId);

    const error = validateServiceForm(serviceItem);
    if (error) {
      return error;
    }
  }

  const seenRoomIds = new Set();
  for (const roomItem of settingsForm.roomOptions) {
    const roomId = String(roomItem.id || "").trim();
    const label = String(roomItem.label || "").trim();

    if (!label) {
      return "Chaque chambre doit avoir un nom.";
    }

    if (!roomId) {
      return "Chaque chambre doit avoir un identifiant valide.";
    }

    if (seenRoomIds.has(roomId)) {
      return "Les identifiants des chambres doivent etre uniques.";
    }
    seenRoomIds.add(roomId);
  }

  return "";
}

function ReservationAdmin() {
  const [capturingId, setCapturingId] = useState("");
  const [captureMessage, setCaptureMessage] = useState("");
  // Handler pour la capture d'empreinte Stripe
  const onCaptureDeposit = async (reservationId) => {
    setCapturingId(reservationId);
    setCaptureMessage("");
    try {
      const result = await captureDepositForReservation(reservationId);
      if (result && result.success) {
        setCaptureMessage("Empreinte capturée avec succès.");
        // Mettre à jour l'état local de la réservation
        setReservations((prev) =>
          prev.map((item) =>
            item._id === reservationId
              ? {
                  ...item,
                  depositStatus: "captured",
                  depositCapturedAt: new Date().toISOString(),
                }
              : item,
          ),
        );
      } else {
        setCaptureMessage(
          result?.message || "Erreur lors de la capture de l'empreinte.",
        );
      }
    } catch (err) {
      setCaptureMessage("Erreur lors de la capture de l'empreinte.");
    } finally {
      setCapturingId("");
    }
  };
  const [activeMenu, setActiveMenu] = useState("requests");
  const [statusFilter, setStatusFilter] = useState("all");
  const [typeFilter, setTypeFilter] = useState("all");
  const [dateFilter, setDateFilter] = useState("");
  const [sortFilter, setSortFilter] = useState("requestedDateAsc");
  const [reservations, setReservations] = useState([]);
  const [loading, setLoading] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");
  const [updatingId, setUpdatingId] = useState("");
  const [deletingId, setDeletingId] = useState("");

  const [reservationRules, setReservationRules] = useState(DEFAULT_RULES);
  const [settingsForm, setSettingsForm] = useState(() =>
    buildSettingsForm(DEFAULT_RULES),
  );
  const [settingsLoading, setSettingsLoading] = useState(true);
  const [settingsSaving, setSettingsSaving] = useState(false);
  const [settingsState, setSettingsState] = useState({ type: "", message: "" });

  const [createFormData, setCreateFormData] = useState(() =>
    buildCreateInitialState(DEFAULT_RULES),
  );
  const [createLoading, setCreateLoading] = useState(false);
  const [createError, setCreateError] = useState("");
  const [createSuccessMessage, setCreateSuccessMessage] = useState("");

  const [openingHoursForm, setOpeningHoursForm] = useState(
    DEFAULT_OPENING_HOURS,
  );
  const [openingHoursLoading, setOpeningHoursLoading] = useState(true);
  const [openingHoursSaving, setOpeningHoursSaving] = useState(false);
  const [openingHoursState, setOpeningHoursState] = useState({
    type: "",
    message: "",
  });

  const activeTableServices = useMemo(
    () =>
      Array.isArray(reservationRules?.tableServices)
        ? reservationRules.tableServices
        : [],
    [reservationRules],
  );

  const activeRooms = useMemo(
    () =>
      Array.isArray(reservationRules?.roomOptions)
        ? reservationRules.roomOptions.filter((roomItem) => roomItem.isActive)
        : [],
    [reservationRules],
  );

  const selectedCreateService = useMemo(
    () =>
      activeTableServices.find(
        (serviceItem) => serviceItem.id === createFormData.tableServiceId,
      ) || activeTableServices[0],
    [activeTableServices, createFormData.tableServiceId],
  );

  const createServiceSlots = useMemo(
    () => getServiceSlotTimes(selectedCreateService),
    [selectedCreateService],
  );

  const emptyMessage = useMemo(() => {
    if (statusFilter === "all") {
      return "Aucune reservation pour le moment.";
    }

    return `Aucune reservation avec le statut "${statusFilter}".`;
  }, [statusFilter]);

  const loadReservations = useCallback(async () => {
    setLoading(true);
    setErrorMessage("");

    try {
      const data = await fetchReservationsForAdmin({
        status: statusFilter,
        reservationType: typeFilter,
        requestedDate: dateFilter,
        sort: sortFilter,
        limit: 200,
      });
      setReservations(data);
    } catch (_error) {
      setErrorMessage("Impossible de charger les reservations.");
    } finally {
      setLoading(false);
    }
  }, [dateFilter, sortFilter, statusFilter, typeFilter]);

  const loadReservationSettings = useCallback(async () => {
    setSettingsLoading(true);
    setSettingsState({ type: "", message: "" });

    try {
      const rules = await fetchReservationSettingsForAdmin();
      setReservationRules(rules);
      setSettingsForm(buildSettingsForm(rules));
      setCreateFormData(buildCreateInitialState(rules));
    } catch (_error) {
      setSettingsState({
        type: "error",
        message: "Impossible de charger les reglages de reservation.",
      });
    } finally {
      setSettingsLoading(false);
    }
  }, []);

  useEffect(() => {
    loadReservations();
  }, [loadReservations]);

  useEffect(() => {
    loadReservationSettings();
  }, [loadReservationSettings]);

  const loadOpeningHours = useCallback(async () => {
    setOpeningHoursLoading(true);
    setOpeningHoursState({ type: "", message: "" });
    try {
      const data = await fetchOpeningHoursForAdmin();
      if (data && Array.isArray(data.days)) {
        setOpeningHoursForm(data);
      }
    } catch (_error) {
      setOpeningHoursState({
        type: "error",
        message: "Impossible de charger les horaires.",
      });
    } finally {
      setOpeningHoursLoading(false);
    }
  }, []);

  useEffect(() => {
    loadOpeningHours();
  }, [loadOpeningHours]);

  useEffect(() => {
    if (createFormData.reservationType !== "table") {
      return;
    }

    if (!createServiceSlots.includes(createFormData.requestedTime)) {
      setCreateFormData((prev) => ({
        ...prev,
        requestedTime: createServiceSlots[0] || "",
      }));
    }
  }, [
    createFormData.requestedTime,
    createFormData.reservationType,
    createServiceSlots,
  ]);

  const onStatusChange = async (reservationId, nextStatus) => {
    setUpdatingId(reservationId);
    setErrorMessage("");

    try {
      const updated = await updateReservationStatus(reservationId, nextStatus);
      if (!updated) {
        return;
      }

      setReservations((prev) =>
        prev.map((item) =>
          item._id === reservationId
            ? { ...item, status: updated.status }
            : item,
        ),
      );
    } catch (_error) {
      setErrorMessage("Impossible de mettre a jour le statut.");
    } finally {
      setUpdatingId("");
    }
  };

  const onDeleteReservation = async (reservationId) => {
    const shouldDelete = window.confirm(
      "Confirmer la suppression de cette reservation ? Cette action est irreversible.",
    );

    if (!shouldDelete) {
      return;
    }

    setDeletingId(reservationId);
    setErrorMessage("");

    try {
      const deletedId = await deleteReservationForAdmin(reservationId);
      if (!deletedId) {
        return;
      }

      setReservations((prev) => prev.filter((item) => item._id !== deletedId));
    } catch (_error) {
      setErrorMessage("Impossible de supprimer la reservation.");
    } finally {
      setDeletingId("");
    }
  };

  const onSettingsServiceChange = (index, field, value) => {
    setSettingsForm((prev) => ({
      ...prev,
      tableServices: prev.tableServices.map((serviceItem, serviceIndex) =>
        serviceIndex === index
          ? {
              ...serviceItem,
              [field]: value,
              ...(field === "label"
                ? {
                    id:
                      String(serviceItem.id || "").startsWith("service-") ||
                      !String(serviceItem.id || "").trim()
                        ? slugify(value) || serviceItem.id
                        : serviceItem.id,
                  }
                : {}),
            }
          : serviceItem,
      ),
    }));
    setSettingsState({ type: "", message: "" });
  };

  const onAddService = () => {
    setSettingsForm((prev) => ({
      ...prev,
      tableServices: [
        ...prev.tableServices,
        createNewService(prev.tableServices.length + 1),
      ],
    }));
  };

  const onRemoveService = (index) => {
    setSettingsForm((prev) => ({
      ...prev,
      tableServices: prev.tableServices.filter(
        (_, serviceIndex) => serviceIndex !== index,
      ),
    }));
  };

  const onSettingsRoomChange = (index, field, value) => {
    setSettingsForm((prev) => ({
      ...prev,
      roomOptions: prev.roomOptions.map((roomItem, roomIndex) =>
        roomIndex === index
          ? {
              ...roomItem,
              [field]: value,
              ...(field === "label"
                ? {
                    id:
                      String(roomItem.id || "").startsWith("chambre-") ||
                      !String(roomItem.id || "").trim()
                        ? slugify(value) || roomItem.id
                        : roomItem.id,
                  }
                : {}),
            }
          : roomItem,
      ),
    }));
    setSettingsState({ type: "", message: "" });
  };

  const onAddRoom = () => {
    setSettingsForm((prev) => ({
      ...prev,
      roomOptions: [
        ...prev.roomOptions,
        createNewRoom(prev.roomOptions.length + 1),
      ],
    }));
  };

  const onRemoveRoom = (index) => {
    setSettingsForm((prev) => ({
      ...prev,
      roomOptions: prev.roomOptions.filter(
        (_, roomIndex) => roomIndex !== index,
      ),
    }));
  };

  const onSaveSettings = async () => {
    const validationError = validateSettingsForm(settingsForm);
    if (validationError) {
      setSettingsState({ type: "error", message: validationError });
      return;
    }

    setSettingsSaving(true);
    setSettingsState({ type: "", message: "" });

    try {
      const payload = {
        tableServices: settingsForm.tableServices.map((serviceItem, index) => ({
          id:
            String(serviceItem.id || "").trim() ||
            slugify(serviceItem.label) ||
            `service-${index + 1}`,
          label: String(serviceItem.label || "").trim(),
          start: String(serviceItem.start || "").trim(),
          end: String(serviceItem.end || "").trim(),
          maxCovers: Number.parseInt(serviceItem.maxCovers, 10),
          maxCoversPerSlot: Number.parseInt(serviceItem.maxCoversPerSlot, 10),
          slotIntervalMinutes: Number.parseInt(
            serviceItem.slotIntervalMinutes,
            10,
          ),
        })),
        roomOptions: settingsForm.roomOptions.map((roomItem, index) => ({
          id:
            String(roomItem.id || "").trim() ||
            slugify(roomItem.label) ||
            `chambre-${index + 1}`,
          label: String(roomItem.label || "").trim(),
          isActive: roomItem.isActive !== false,
        })),
      };

      const savedRules = await saveReservationSettingsForAdmin(payload);
      setReservationRules(savedRules);
      setSettingsForm(buildSettingsForm(savedRules));
      setCreateFormData(buildCreateInitialState(savedRules));
      setSettingsState({
        type: "success",
        message: "Reglages enregistres avec succes.",
      });
    } catch (_error) {
      setSettingsState({
        type: "error",
        message: "Impossible d'enregistrer les reglages.",
      });
    } finally {
      setSettingsSaving(false);
    }
  };

  const onOpeningHoursDayChange = (dayIndex, field, value) => {
    setOpeningHoursForm((prev) => ({
      ...prev,
      days: prev.days.map((day) =>
        day.dayIndex === dayIndex ? { ...day, [field]: value } : day,
      ),
    }));
    setOpeningHoursState({ type: "", message: "" });
  };

  const onOpeningHoursSlotChange = (dayIndex, slotIndex, field, value) => {
    setOpeningHoursForm((prev) => ({
      ...prev,
      days: prev.days.map((day) =>
        day.dayIndex === dayIndex
          ? {
              ...day,
              slots: day.slots.map((slot, si) =>
                si === slotIndex ? { ...slot, [field]: value } : slot,
              ),
            }
          : day,
      ),
    }));
    setOpeningHoursState({ type: "", message: "" });
  };

  const onAddSlot = (dayIndex) => {
    setOpeningHoursForm((prev) => ({
      ...prev,
      days: prev.days.map((day) =>
        day.dayIndex === dayIndex
          ? { ...day, slots: [...day.slots, { open: "12:00", close: "14:00" }] }
          : day,
      ),
    }));
  };

  const onRemoveSlot = (dayIndex, slotIndex) => {
    setOpeningHoursForm((prev) => ({
      ...prev,
      days: prev.days.map((day) =>
        day.dayIndex === dayIndex
          ? { ...day, slots: day.slots.filter((_, si) => si !== slotIndex) }
          : day,
      ),
    }));
  };

  const onSaveOpeningHours = async () => {
    setOpeningHoursSaving(true);
    setOpeningHoursState({ type: "", message: "" });
    try {
      const saved = await saveOpeningHoursToApi(openingHoursForm);
      if (saved && Array.isArray(saved.days)) {
        setOpeningHoursForm(saved);
      }
      setOpeningHoursState({
        type: "success",
        message: "Horaires enregistres avec succes.",
      });
    } catch (_error) {
      setOpeningHoursState({
        type: "error",
        message: "Impossible d'enregistrer les horaires.",
      });
    } finally {
      setOpeningHoursSaving(false);
    }
  };

  const onCreateChange = (event) => {
    const { name, value } = event.target;

    if (name === "reservationType") {
      setCreateFormData((prev) => ({
        ...prev,
        reservationType: value,
        tableServiceId:
          value === "table"
            ? prev.tableServiceId || getFirstServiceId(reservationRules)
            : prev.tableServiceId,
        roomId:
          value === "chambre"
            ? prev.roomId || getFirstActiveRoomId(reservationRules)
            : prev.roomId,
      }));
    } else if (name === "tableServiceId") {
      const service = activeTableServices.find(
        (serviceItem) => serviceItem.id === value,
      );
      const slots = getServiceSlotTimes(service);

      setCreateFormData((prev) => ({
        ...prev,
        tableServiceId: value,
        requestedTime: slots[0] || "",
      }));
    } else {
      setCreateFormData((prev) => ({ ...prev, [name]: value }));
    }

    setCreateError("");
    setCreateSuccessMessage("");
  };

  const validateCreateForm = () => {
    if (!createFormData.requestedDate) {
      return "Date de reservation invalide.";
    }

    const covers = Number.parseInt(createFormData.guestCount, 10);
    if (!Number.isInteger(covers) || covers < 1 || covers > 20) {
      return "Le nombre de couverts doit etre compris entre 1 et 20.";
    }

    if (createFormData.reservationType === "table") {
      if (!selectedCreateService?.id) {
        return "Veuillez choisir un service table.";
      }

      if (!TIME_REGEX.test(String(createFormData.requestedTime || ""))) {
        return "Veuillez choisir un horaire valide.";
      }
    }

    if (createFormData.reservationType === "chambre") {
      const nights = Number.parseInt(createFormData.nightCount, 10);
      if (!Number.isInteger(nights) || nights < 1 || nights > 30) {
        return "Le nombre de nuits doit etre compris entre 1 et 30.";
      }

      if (
        activeRooms.length > 0 &&
        !String(createFormData.roomId || "").trim()
      ) {
        return "Veuillez choisir une chambre.";
      }
    }

    if (createFormData.sourceOption === "autre") {
      if (!String(createFormData.sourceCustom || "").trim()) {
        return "Veuillez renseigner la source personnalisee.";
      }
    }

    const email = String(createFormData.email || "").trim();
    if (email && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
      return "Adresse email invalide.";
    }

    const phone = String(createFormData.phone || "").trim();
    if (phone && !/^[0-9+()\-\s.]{6,40}$/.test(phone)) {
      return "Numero de telephone invalide.";
    }

    return "";
  };

  const onCreateReservation = async (event) => {
    event.preventDefault();
    setCreateError("");
    setCreateSuccessMessage("");

    const validationError = validateCreateForm();
    if (validationError) {
      setCreateError(validationError);
      return;
    }

    setCreateLoading(true);

    try {
      const source =
        createFormData.sourceOption === "autre"
          ? String(createFormData.sourceCustom || "").trim()
          : createFormData.sourceOption;

      const payload = {
        reservationType: createFormData.reservationType,
        firstName: createFormData.firstName,
        lastName: createFormData.lastName,
        email: createFormData.email,
        phone: createFormData.phone,
        requestedDate: createFormData.requestedDate,
        requestedTime:
          createFormData.reservationType === "table"
            ? createFormData.requestedTime
            : "",
        tableServiceId:
          createFormData.reservationType === "table"
            ? createFormData.tableServiceId
            : "",
        guestCount: Number.parseInt(createFormData.guestCount, 10),
        nightCount:
          createFormData.reservationType === "chambre"
            ? Number.parseInt(createFormData.nightCount, 10)
            : 1,
        roomId:
          createFormData.reservationType === "chambre"
            ? createFormData.roomId
            : "",
        source,
        status: createFormData.status,
        message: createFormData.message,
      };

      await createReservationByAdmin(payload);
      setCreateSuccessMessage("Reservation creee avec succes.");
      setCreateFormData(buildCreateInitialState(reservationRules));
      setActiveMenu("requests");
      await loadReservations();
    } catch (error) {
      setCreateError(
        error?.payload?.error ||
          error?.message ||
          "Impossible de creer la reservation.",
      );
    } finally {
      setCreateLoading(false);
    }
  };

  return (
    <div className="dashboard reservations-admin-page">
      <div className="dashboard-header">
        <h1>Reservations</h1>
        <p className="dashboard-subtitle">
          Gestion separee des demandes, de la creation, des services et des
          chambres.
        </p>
      </div>

      <div className="reservation-admin-tabs">
        {ADMIN_MENUS.map((menuItem) => (
          <button
            key={menuItem.id}
            type="button"
            className={`reservation-admin-tab${activeMenu === menuItem.id ? " active" : ""}`}
            onClick={() => setActiveMenu(menuItem.id)}
          >
            {menuItem.label}
          </button>
        ))}
      </div>

      {settingsState.message && (
        <p className={`reservations-state ${settingsState.type || ""}`}>
          {settingsState.message}
        </p>
      )}

      {createSuccessMessage && (
        <p className="reservations-state success">{createSuccessMessage}</p>
      )}

      {activeMenu === "requests" && (
        <>
          <div className="reservations-toolbar">
            <div className="reservations-filters">
              <button
                type="button"
                className={`status-chip${statusFilter === "all" ? " active" : ""}`}
                onClick={() => setStatusFilter("all")}
              >
                Tous
              </button>
              <button
                type="button"
                className={`status-chip${statusFilter === "pending" ? " active" : ""}`}
                onClick={() => setStatusFilter("pending")}
              >
                En attente
              </button>
              <button
                type="button"
                className={`status-chip${statusFilter === "confirmed" ? " active" : ""}`}
                onClick={() => setStatusFilter("confirmed")}
              >
                Confirmees
              </button>
              <button
                type="button"
                className={`status-chip${statusFilter === "cancelled" ? " active" : ""}`}
                onClick={() => setStatusFilter("cancelled")}
              >
                Annulees
              </button>
              <button
                type="button"
                className={`status-chip${statusFilter === "no_show" ? " active" : ""}`}
                onClick={() => setStatusFilter("no_show")}
              >
                No-show
              </button>
            </div>

            <div className="reservations-toolbar-actions">
              <button
                type="button"
                className="refresh-btn"
                onClick={loadReservations}
              >
                Rafraichir
              </button>
            </div>
          </div>

          <div className="reservations-advanced-filters">
            <div className="reservations-filter-group">
              <span className="filter-label">Categorie</span>
              <div className="reservations-filters">
                {TYPE_OPTIONS.map((typeValue) => (
                  <button
                    key={typeValue}
                    type="button"
                    className={`status-chip${typeFilter === typeValue ? " active" : ""}`}
                    onClick={() => setTypeFilter(typeValue)}
                  >
                    {typeValue === "all" ? "Tous" : typeValue}
                  </button>
                ))}
              </div>
            </div>

            <div className="reservations-filter-row">
              <label className="filter-label" htmlFor="reservation-date-filter">
                Date
              </label>
              <input
                id="reservation-date-filter"
                type="date"
                value={dateFilter}
                onChange={(event) => setDateFilter(event.target.value)}
                className="reservation-filter-input"
              />
              {dateFilter && (
                <button
                  type="button"
                  className="refresh-btn"
                  onClick={() => setDateFilter("")}
                >
                  Effacer
                </button>
              )}
            </div>

            <div className="reservations-filter-row">
              <label className="filter-label" htmlFor="reservation-sort-filter">
                Tri
              </label>
              <select
                id="reservation-sort-filter"
                className="reservation-filter-input"
                value={sortFilter}
                onChange={(event) => setSortFilter(event.target.value)}
              >
                {SORT_OPTIONS.map((option) => (
                  <option key={option.value} value={option.value}>
                    {option.label}
                  </option>
                ))}
              </select>
            </div>
          </div>

          {loading && <p className="reservations-state">Chargement...</p>}
          {errorMessage && (
            <p className="reservations-state error">{errorMessage}</p>
          )}

          {!loading && !errorMessage && reservations.length === 0 && (
            <p className="reservations-state">{emptyMessage}</p>
          )}

          {!loading && !errorMessage && reservations.length > 0 && (
            <div className="reservations-grid">
              {reservations.map((reservation) => {
                const isUpdating = updatingId === reservation._id;
                const isRoomReservation =
                  reservation.reservationType === "chambre";
                const reservationTypeLabel = isRoomReservation
                  ? "Chambre"
                  : "Restaurant";
                const customerName =
                  reservation.fullName ||
                  `${reservation.firstName || ""} ${reservation.lastName || ""}`.trim() ||
                  "Sans nom";

                return (
                  <article
                    key={reservation._id}
                    className={`reservation-card ${
                      isRoomReservation
                        ? "reservation-card-chambre"
                        : "reservation-card-restaurant"
                    }`}
                  >
                    <header className="reservation-card-header">
                      <h3>{customerName}</h3>
                      <div className="reservation-card-badges">
                        <span
                          className={`reservation-type-badge ${
                            isRoomReservation ? "chambre" : "restaurant"
                          }`}
                        >
                          {reservationTypeLabel}
                        </span>
                        <span
                          className={`reservation-badge ${reservation.status}`}
                        >
                          {reservation.status}
                        </span>
                      </div>
                    </header>

                    <div className="reservation-meta">
                      <p>
                        <strong>Type:</strong> {reservationTypeLabel}
                      </p>
                      <p>
                        <strong>Date demandee:</strong>{" "}
                        {formatDate(reservation.requestedDate)}
                      </p>
                      {reservation.reservationType === "table" && (
                        <p>
                          <strong>Service / heure:</strong>{" "}
                          {reservation.tableServiceLabel ||
                            reservation.tableServiceId ||
                            "-"}
                          {reservation.requestedTime
                            ? ` - ${reservation.requestedTime}`
                            : ""}
                        </p>
                      )}
                      {reservation.reservationType === "chambre" && (
                        <p>
                          <strong>Chambre:</strong>{" "}
                          {reservation.roomLabel || reservation.roomId || "-"}
                        </p>
                      )}
                      <p>
                        <strong>Personnes:</strong> {reservation.guestCount}
                        {reservation.reservationType === "chambre"
                          ? ` - ${reservation.nightCount} nuit(s)`
                          : ""}
                      </p>
                      <p>
                        <strong>Email:</strong> {reservation.email || "-"}
                      </p>
                      <p>
                        <strong>Telephone:</strong> {reservation.phone || "-"}
                      </p>
                      <p>
                        <strong>Source:</strong> {reservation.source || "site"}
                      </p>
                      <p>
                        <strong>Recue le:</strong>{" "}
                        {formatDateTime(reservation.createdAt)}
                      </p>

                      <div className="reservation-deposit-meta">
                        <p>
                          <strong>Caution:</strong>{" "}
                          {reservation.depositRequired
                            ? formatCurrency(
                                reservation.depositAmountCents,
                                reservation.depositCurrency,
                              )
                            : "Non requise"}
                        </p>
                        <p>
                          <strong>Etat caution:</strong>{" "}
                          <span
                            className={`reservation-badge reservation-badge-deposit ${String(
                              reservation.depositStatus || "not_required",
                            ).toLowerCase()}`}
                          >
                            {getDepositStatusLabel(reservation.depositStatus)}
                          </span>
                        </p>
                        {reservation.depositAuthorizedAt && (
                          <p>
                            <strong>Autorisee le:</strong>{" "}
                            {formatDateTime(reservation.depositAuthorizedAt)}
                          </p>
                        )}
                        {reservation.depositCapturedAt && (
                          <p>
                            <strong>Capturee le:</strong>{" "}
                            {formatDateTime(reservation.depositCapturedAt)}
                          </p>
                        )}
                        {reservation.depositReleasedAt && (
                          <p>
                            <strong>Liberee le:</strong>{" "}
                            {formatDateTime(reservation.depositReleasedAt)}
                          </p>
                        )}
                        {reservation.depositLastError && (
                          <p className="reservation-deposit-error">
                            <strong>Erreur caution:</strong>{" "}
                            {reservation.depositLastError}
                          </p>
                        )}
                      </div>
                    </div>

                    {reservation.message ? (
                      <p className="reservation-message">
                        {reservation.message}
                      </p>
                    ) : (
                      <p className="reservation-message muted">
                        Aucun message.
                      </p>
                    )}

                    <div className="reservation-actions">
                      {/* Bouton de capture d'empreinte Stripe */}
                      {reservation.depositRequired &&
                        reservation.depositStatus === "authorized" && (
                          <button
                            type="button"
                            className="reservation-capture-btn"
                            disabled={
                              capturingId === reservation._id ||
                              isUpdating ||
                              deletingId === reservation._id
                            }
                            onClick={() => onCaptureDeposit(reservation._id)}
                          >
                            {capturingId === reservation._id
                              ? "Capture..."
                              : "Capturer l'empreinte"}
                          </button>
                        )}
                      {/* Message de feedback capture */}
                      {captureMessage && capturingId === "" && (
                        <p className="reservation-capture-message">
                          {captureMessage}
                        </p>
                      )}
                      <label htmlFor={`status-${reservation._id}`}>
                        Statut
                      </label>
                      <select
                        id={`status-${reservation._id}`}
                        value={reservation.status}
                        disabled={isUpdating || deletingId === reservation._id}
                        onChange={(event) =>
                          onStatusChange(reservation._id, event.target.value)
                        }
                      >
                        {STATUS_OPTIONS.map((option) => (
                          <option key={option} value={option}>
                            {option}
                          </option>
                        ))}
                      </select>

                      <button
                        type="button"
                        className="reservation-delete-btn"
                        disabled={deletingId === reservation._id || isUpdating}
                        onClick={() => onDeleteReservation(reservation._id)}
                      >
                        {deletingId === reservation._id
                          ? "Suppression..."
                          : "Supprimer"}
                      </button>
                    </div>
                  </article>
                );
              })}
            </div>
          )}
        </>
      )}

      {activeMenu === "create" && (
        <section className="reservation-settings-panel">
          <div className="reservation-settings-header">
            <h2>Creation manuelle</h2>
          </div>

          <form
            className="reservation-create-form"
            onSubmit={onCreateReservation}
            noValidate
          >
            <div className="row">
              <div className="col-sm-6 col-xs-12">
                <div className="form-group">
                  <label htmlFor="create-reservation-type">Type</label>
                  <select
                    id="create-reservation-type"
                    className="reservation-filter-input"
                    name="reservationType"
                    value={createFormData.reservationType}
                    onChange={onCreateChange}
                  >
                    <option value="table">table</option>
                    <option value="chambre">chambre</option>
                  </select>
                </div>
              </div>

              <div className="col-sm-6 col-xs-12">
                <div className="form-group">
                  <label htmlFor="create-status">Statut</label>
                  <select
                    id="create-status"
                    className="reservation-filter-input"
                    name="status"
                    value={createFormData.status}
                    onChange={onCreateChange}
                  >
                    {STATUS_OPTIONS.map((statusValue) => (
                      <option key={statusValue} value={statusValue}>
                        {statusValue}
                      </option>
                    ))}
                  </select>
                </div>
              </div>
            </div>

            <div className="row">
              <div className="col-sm-6 col-xs-12">
                <div className="form-group">
                  <label htmlFor="create-first-name">Prenom (optionnel)</label>
                  <input
                    id="create-first-name"
                    className="reservation-filter-input"
                    type="text"
                    name="firstName"
                    value={createFormData.firstName}
                    onChange={onCreateChange}
                    maxLength={80}
                  />
                </div>
              </div>

              <div className="col-sm-6 col-xs-12">
                <div className="form-group">
                  <label htmlFor="create-last-name">Nom (optionnel)</label>
                  <input
                    id="create-last-name"
                    className="reservation-filter-input"
                    type="text"
                    name="lastName"
                    value={createFormData.lastName}
                    onChange={onCreateChange}
                    maxLength={80}
                  />
                </div>
              </div>
            </div>

            <div className="row">
              <div className="col-sm-6 col-xs-12">
                <div className="form-group">
                  <label htmlFor="create-email">Email (optionnel)</label>
                  <input
                    id="create-email"
                    className="reservation-filter-input"
                    type="email"
                    name="email"
                    value={createFormData.email}
                    onChange={onCreateChange}
                    maxLength={180}
                  />
                </div>
              </div>

              <div className="col-sm-6 col-xs-12">
                <div className="form-group">
                  <label htmlFor="create-phone">Telephone (optionnel)</label>
                  <input
                    id="create-phone"
                    className="reservation-filter-input"
                    type="tel"
                    name="phone"
                    value={createFormData.phone}
                    onChange={onCreateChange}
                    maxLength={40}
                  />
                </div>
              </div>
            </div>

            <div className="row">
              <div className="col-sm-6 col-xs-12">
                <div className="form-group">
                  <label htmlFor="create-requested-date">Date *</label>
                  <input
                    id="create-requested-date"
                    className="reservation-filter-input"
                    type="date"
                    name="requestedDate"
                    value={createFormData.requestedDate}
                    onChange={onCreateChange}
                    required
                  />
                </div>
              </div>

              {createFormData.reservationType === "table" ? (
                <div className="col-sm-6 col-xs-12">
                  <div className="form-group">
                    <label htmlFor="create-table-service">Service *</label>
                    <select
                      id="create-table-service"
                      className="reservation-filter-input"
                      name="tableServiceId"
                      value={createFormData.tableServiceId}
                      onChange={onCreateChange}
                    >
                      {activeTableServices.map((serviceItem) => (
                        <option key={serviceItem.id} value={serviceItem.id}>
                          {serviceItem.label} ({serviceItem.start} -{" "}
                          {serviceItem.end})
                        </option>
                      ))}
                    </select>
                  </div>
                </div>
              ) : (
                <div className="col-sm-6 col-xs-12">
                  <div className="form-group">
                    <label htmlFor="create-room-id">Chambre *</label>
                    <select
                      id="create-room-id"
                      className="reservation-filter-input"
                      name="roomId"
                      value={createFormData.roomId}
                      onChange={onCreateChange}
                      disabled={activeRooms.length === 0}
                      required
                    >
                      {activeRooms.length === 0 && (
                        <option value="">Aucune chambre active</option>
                      )}
                      {activeRooms.map((roomItem) => (
                        <option key={roomItem.id} value={roomItem.id}>
                          {roomItem.label}
                        </option>
                      ))}
                    </select>
                  </div>
                </div>
              )}
            </div>

            {createFormData.reservationType === "table" && (
              <div className="row">
                <div className="col-sm-6 col-xs-12">
                  <div className="form-group">
                    <label htmlFor="create-requested-time">Horaire *</label>
                    <select
                      id="create-requested-time"
                      className="reservation-filter-input"
                      name="requestedTime"
                      value={createFormData.requestedTime}
                      onChange={onCreateChange}
                    >
                      {createServiceSlots.map((slot) => (
                        <option key={slot} value={slot}>
                          {slot}
                        </option>
                      ))}
                    </select>
                  </div>
                </div>
              </div>
            )}

            {createFormData.reservationType === "chambre" && (
              <div className="row">
                <div className="col-sm-6 col-xs-12">
                  <div className="form-group">
                    <label htmlFor="create-night-count">
                      Nombre de nuits *
                    </label>
                    <input
                      id="create-night-count"
                      className="reservation-filter-input"
                      type="number"
                      name="nightCount"
                      value={createFormData.nightCount}
                      onChange={onCreateChange}
                      min="1"
                      max="30"
                      required
                    />
                  </div>
                </div>
              </div>
            )}

            <div className="row">
              <div className="col-sm-6 col-xs-12">
                <div className="form-group">
                  <label htmlFor="create-guest-count">Couverts *</label>
                  <input
                    id="create-guest-count"
                    className="reservation-filter-input"
                    type="number"
                    name="guestCount"
                    value={createFormData.guestCount}
                    onChange={onCreateChange}
                    min="1"
                    max="20"
                    required
                  />
                </div>
              </div>

              <div className="col-sm-6 col-xs-12">
                <div className="form-group">
                  <label htmlFor="create-source-option">Source *</label>
                  <select
                    id="create-source-option"
                    className="reservation-filter-input"
                    name="sourceOption"
                    value={createFormData.sourceOption}
                    onChange={onCreateChange}
                  >
                    {SOURCE_OPTIONS.map((sourceOption) => (
                      <option
                        key={sourceOption.value}
                        value={sourceOption.value}
                      >
                        {sourceOption.label}
                      </option>
                    ))}
                  </select>
                </div>
              </div>
            </div>

            {createFormData.sourceOption === "autre" && (
              <div className="row">
                <div className="col-sm-12">
                  <div className="form-group">
                    <label htmlFor="create-source-custom">
                      Source personnalisee *
                    </label>
                    <input
                      id="create-source-custom"
                      className="reservation-filter-input"
                      type="text"
                      name="sourceCustom"
                      value={createFormData.sourceCustom}
                      onChange={onCreateChange}
                      maxLength={80}
                      required
                    />
                  </div>
                </div>
              </div>
            )}

            <div className="row">
              <div className="col-sm-12">
                <div className="form-group">
                  <label htmlFor="create-message">Message (optionnel)</label>
                  <textarea
                    id="create-message"
                    className="reservation-filter-input reservation-admin-message"
                    name="message"
                    value={createFormData.message}
                    onChange={onCreateChange}
                    rows="4"
                    maxLength={1200}
                  />
                </div>
              </div>
            </div>

            {createError && (
              <p className="reservations-state error">{createError}</p>
            )}

            <div className="reservation-settings-footer">
              <button
                type="submit"
                className="refresh-btn reservation-open-create-btn"
                disabled={createLoading}
              >
                {createLoading ? "Creation..." : "Enregistrer la reservation"}
              </button>
            </div>
          </form>
        </section>
      )}

      {activeMenu === "services" && (
        <section className="reservation-settings-panel">
          <div className="reservation-settings-header">
            <h2>Reglages des services de table</h2>
            <div className="reservations-toolbar-actions">
              <button
                type="button"
                className="refresh-btn"
                onClick={loadReservationSettings}
                disabled={settingsLoading || settingsSaving}
              >
                Recharger
              </button>
              <button
                type="button"
                className="refresh-btn"
                onClick={onAddService}
              >
                Ajouter un service
              </button>
            </div>
          </div>

          {settingsLoading ? (
            <p className="reservations-state">Chargement des reglages...</p>
          ) : (
            <div className="reservation-settings-grid">
              {settingsForm.tableServices.map((serviceItem, index) => (
                <div
                  key={`${serviceItem.id}-${index}`}
                  className="reservation-settings-item"
                >
                  <div className="form-group">
                    <label htmlFor={`service-label-${index}`}>
                      Nom du service
                    </label>
                    <input
                      id={`service-label-${index}`}
                      className="reservation-filter-input"
                      type="text"
                      value={serviceItem.label}
                      onChange={(event) =>
                        onSettingsServiceChange(
                          index,
                          "label",
                          event.target.value,
                        )
                      }
                      maxLength={80}
                    />
                  </div>

                  <div className="form-group">
                    <label htmlFor={`service-start-${index}`}>Debut</label>
                    <input
                      id={`service-start-${index}`}
                      className="reservation-filter-input"
                      type="time"
                      value={serviceItem.start}
                      onChange={(event) =>
                        onSettingsServiceChange(
                          index,
                          "start",
                          event.target.value,
                        )
                      }
                    />
                  </div>

                  <div className="form-group">
                    <label htmlFor={`service-end-${index}`}>Fin</label>
                    <input
                      id={`service-end-${index}`}
                      className="reservation-filter-input"
                      type="time"
                      value={serviceItem.end}
                      onChange={(event) =>
                        onSettingsServiceChange(
                          index,
                          "end",
                          event.target.value,
                        )
                      }
                    />
                  </div>

                  <div className="form-group">
                    <label htmlFor={`service-max-${index}`}>
                      Couverts max service
                    </label>
                    <input
                      id={`service-max-${index}`}
                      className="reservation-filter-input"
                      type="number"
                      min="1"
                      max="500"
                      value={serviceItem.maxCovers}
                      onChange={(event) =>
                        onSettingsServiceChange(
                          index,
                          "maxCovers",
                          event.target.value,
                        )
                      }
                    />
                  </div>

                  <div className="form-group">
                    <label htmlFor={`service-slot-max-${index}`}>
                      Couverts max par creneau
                    </label>
                    <input
                      id={`service-slot-max-${index}`}
                      className="reservation-filter-input"
                      type="number"
                      min="1"
                      max="100"
                      value={serviceItem.maxCoversPerSlot}
                      onChange={(event) =>
                        onSettingsServiceChange(
                          index,
                          "maxCoversPerSlot",
                          event.target.value,
                        )
                      }
                    />
                  </div>

                  <div className="form-group">
                    <label htmlFor={`service-interval-${index}`}>
                      Intervalle creneaux (minutes)
                    </label>
                    <input
                      id={`service-interval-${index}`}
                      className="reservation-filter-input"
                      type="number"
                      min="5"
                      max="60"
                      value={serviceItem.slotIntervalMinutes}
                      onChange={(event) =>
                        onSettingsServiceChange(
                          index,
                          "slotIntervalMinutes",
                          event.target.value,
                        )
                      }
                    />
                  </div>

                  <div className="reservation-settings-actions">
                    <button
                      type="button"
                      className="reservation-delete-btn"
                      onClick={() => onRemoveService(index)}
                      disabled={settingsForm.tableServices.length <= 1}
                    >
                      Supprimer
                    </button>
                  </div>
                </div>
              ))}
            </div>
          )}

          <div className="reservation-settings-footer">
            <button
              type="button"
              className="refresh-btn reservation-open-create-btn"
              onClick={onSaveSettings}
              disabled={settingsSaving}
            >
              {settingsSaving
                ? "Enregistrement..."
                : "Enregistrer les services"}
            </button>
          </div>

          <hr style={{ margin: "2rem 0", borderColor: "#e0d6cc" }} />

          <div className="reservation-settings-header">
            <h2>Horaires d&apos;ouverture (page d&apos;accueil)</h2>
            <div className="reservations-toolbar-actions">
              <button
                type="button"
                className="refresh-btn"
                onClick={loadOpeningHours}
                disabled={openingHoursLoading || openingHoursSaving}
              >
                Recharger
              </button>
            </div>
          </div>

          {openingHoursState.message && (
            <p className={`reservations-state ${openingHoursState.type || ""}`}>
              {openingHoursState.message}
            </p>
          )}

          {openingHoursLoading ? (
            <p className="reservations-state">Chargement des horaires...</p>
          ) : (
            <div className="reservation-settings-grid">
              {openingHoursForm.days.map((day) => (
                <div key={day.dayIndex} className="reservation-settings-item">
                  <div className="form-group">
                    <label style={{ fontWeight: 700, fontSize: "1.05em" }}>
                      {day.label}
                    </label>
                  </div>

                  <label className="reservation-switch">
                    <input
                      type="checkbox"
                      checked={!day.isClosed}
                      onChange={(e) =>
                        onOpeningHoursDayChange(
                          day.dayIndex,
                          "isClosed",
                          !e.target.checked,
                        )
                      }
                    />
                    <span>{day.isClosed ? "Ferme" : "Ouvert"}</span>
                  </label>

                  {!day.isClosed && (
                    <>
                      {day.slots.map((slot, slotIdx) => (
                        <div
                          key={slotIdx}
                          style={{
                            display: "flex",
                            gap: "0.5rem",
                            alignItems: "center",
                            flexWrap: "wrap",
                          }}
                        >
                          <div
                            className="form-group"
                            style={{ flex: 1, minWidth: "100px" }}
                          >
                            <label
                              htmlFor={`oh-open-${day.dayIndex}-${slotIdx}`}
                            >
                              Ouverture
                            </label>
                            <input
                              id={`oh-open-${day.dayIndex}-${slotIdx}`}
                              className="reservation-filter-input"
                              type="time"
                              value={slot.open}
                              onChange={(e) =>
                                onOpeningHoursSlotChange(
                                  day.dayIndex,
                                  slotIdx,
                                  "open",
                                  e.target.value,
                                )
                              }
                            />
                          </div>
                          <div
                            className="form-group"
                            style={{ flex: 1, minWidth: "100px" }}
                          >
                            <label
                              htmlFor={`oh-close-${day.dayIndex}-${slotIdx}`}
                            >
                              Fermeture
                            </label>
                            <input
                              id={`oh-close-${day.dayIndex}-${slotIdx}`}
                              className="reservation-filter-input"
                              type="time"
                              value={slot.close}
                              onChange={(e) =>
                                onOpeningHoursSlotChange(
                                  day.dayIndex,
                                  slotIdx,
                                  "close",
                                  e.target.value,
                                )
                              }
                            />
                          </div>
                          {day.slots.length > 1 && (
                            <button
                              type="button"
                              className="reservation-delete-btn"
                              style={{ marginTop: "1.2rem" }}
                              onClick={() =>
                                onRemoveSlot(day.dayIndex, slotIdx)
                              }
                            >
                              ×
                            </button>
                          )}
                        </div>
                      ))}
                      <button
                        type="button"
                        className="refresh-btn"
                        style={{ marginTop: "0.3rem", fontSize: "0.85em" }}
                        onClick={() => onAddSlot(day.dayIndex)}
                      >
                        + Ajouter un creneau
                      </button>
                    </>
                  )}
                </div>
              ))}
            </div>
          )}

          <div className="reservation-settings-footer">
            <button
              type="button"
              className="refresh-btn reservation-open-create-btn"
              onClick={onSaveOpeningHours}
              disabled={openingHoursSaving}
            >
              {openingHoursSaving
                ? "Enregistrement..."
                : "Enregistrer les horaires"}
            </button>
          </div>
        </section>
      )}

      {activeMenu === "rooms" && (
        <section className="reservation-settings-panel">
          <div className="reservation-settings-header">
            <h2>Reglages des chambres</h2>
            <div className="reservations-toolbar-actions">
              <button
                type="button"
                className="refresh-btn"
                onClick={loadReservationSettings}
                disabled={settingsLoading || settingsSaving}
              >
                Recharger
              </button>
              <button type="button" className="refresh-btn" onClick={onAddRoom}>
                Ajouter une chambre
              </button>
            </div>
          </div>

          {settingsLoading ? (
            <p className="reservations-state">Chargement des reglages...</p>
          ) : (
            <div className="reservation-settings-grid">
              {settingsForm.roomOptions.map((roomItem, index) => (
                <div
                  key={`${roomItem.id}-${index}`}
                  className="reservation-settings-item"
                >
                  <div className="form-group">
                    <label htmlFor={`room-label-${index}`}>
                      Nom de chambre
                    </label>
                    <input
                      id={`room-label-${index}`}
                      className="reservation-filter-input"
                      type="text"
                      value={roomItem.label}
                      onChange={(event) =>
                        onSettingsRoomChange(index, "label", event.target.value)
                      }
                      maxLength={120}
                    />
                  </div>

                  <label className="reservation-switch">
                    <input
                      type="checkbox"
                      checked={roomItem.isActive}
                      onChange={(event) =>
                        onSettingsRoomChange(
                          index,
                          "isActive",
                          event.target.checked,
                        )
                      }
                    />
                    <span>Disponible a la reservation</span>
                  </label>

                  <div className="reservation-settings-actions">
                    <button
                      type="button"
                      className="reservation-delete-btn"
                      onClick={() => onRemoveRoom(index)}
                      disabled={settingsForm.roomOptions.length <= 1}
                    >
                      Supprimer
                    </button>
                  </div>
                </div>
              ))}
            </div>
          )}

          <div className="reservation-settings-footer">
            <button
              type="button"
              className="refresh-btn reservation-open-create-btn"
              onClick={onSaveSettings}
              disabled={settingsSaving}
            >
              {settingsSaving
                ? "Enregistrement..."
                : "Enregistrer les chambres"}
            </button>
          </div>
        </section>
      )}
    </div>
  );
}

export default ReservationAdmin;
